import React, { useState } from 'react';
import { IAuthState, IRootState } from '../../../../../@types/redux';
import { connect } from 'react-redux';
import AppFunctionsService from '../../../../../services/appFunctionsService';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../../../@types/model/transformer/invoice/transformerInvoice';
import LPUCapturingTable from '../../table/LPUCaptureTable';
import { ILPULine, ISPULine } from '../../../../../@types/model/transformer/tariffs/tariffs';
import TariffConfirmTable from '../../table/ConfirmTable';
import GeneralFunctions from '../../../../../store/general/functions';
import moment from 'moment';
import SPUCapturingTable from '../../table/SPUCaptureTable';
import ProjectDialog from '../../../../customComponentV2/dialog/ProjectDialog';
import DialogContent from '@material-ui/core/DialogContent';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';

interface ITransformerLPUEditDialogProps {
    auth : IAuthState;
    invoice : ITransformerInvoice;
}

const TransformerEditDialogComponent = (props : ITransformerLPUEditDialogProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedInvoice, setSelectedInvoice] = useState<ITransformerInvoice>(props.invoice);
    const [showCapture, setShowCapture] = useState<boolean>(true);

    const onClick = (): void => {
        if (props.invoice.journaled) return;
        setOpen(true);
        setIsLoading(false);
        setSelectedInvoice({
            ...props.invoice,
            lines: [
                ...props.invoice.lines,
            ],
            transformer: {
                ...props.invoice.transformer,
            },
        });
    };

    const onClose = (): void => {
        setOpen(false);
        setShowCapture(true);
        setIsLoading(false);
    };
    
    const onCaptureSubmit = <T extends ISPULine | ILPULine>(invoices : Array<ITransformerInvoice<T>>): void => {
        if (invoices.length === 0) return;

        setSelectedInvoice(invoices[0]);
        setShowCapture(false);
    };
    
    const onCancelClick = (): void => {
        setShowCapture(true);
    };

    const onConfirmClick = (): void => {
        save();
    };

    const save = async (): Promise<void> => {
        const { auth } = props;

        if (!auth.session?.firebaseUser) 
            return;

        setIsLoading(true);

        try {
            const updatedInvoice = {
                ...selectedInvoice,
                lines: [ ...selectedInvoice.lines ],
                updatedBy: auth.session.firebaseUser.uid,
                updatedByEmployee: auth.session.employee.EmployeeNumberEmployee?.toString() ?? '',
                updatedByName: auth.session.employee.NameName?.toString() ?? '',
                updatedOn: moment.utc().valueOf(),
            };

            await TransformerInvoiceHelper.saveMany([updatedInvoice]);
            onClose();
            GeneralFunctions.generalShowSuccessSnackbar('Success');
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, `${ex}`);
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Tooltip title='Edit'>
                <div>
                    <IconButton className={'cp'} onClick={props.invoice.journaled ? undefined : onClick}
                        aria-label='Edit'>
                        <EditIcon />
                    </IconButton>
                </div>
            </Tooltip>
            <ProjectDialog
                isOpen={open}
                isLoadingCircular={isLoading}
                onClose={onClose}
                title={`EDIT INVOICE | ${AppFunctionsService.formatDateTimeToDateOnly(props.invoice.monthDate)} | ${props.invoice.epNumber}`}
                fullScreen
            >
                <DialogContent className='fdc flx1 ais hfill bcg0'>
                    {
                        showCapture &&
                        props.invoice.type === 'lpu' &&
                        <div className='fdc hfill mh0 mw0'>
                            <LPUCapturingTable
                                invoices={[selectedInvoice] as Array<ITransformerInvoice<ILPULine>>}
                                disabledTransformers={[]}
                                multi={props.invoice.lines.length > 1}
                                onSubmit={onCaptureSubmit}
                                onCancelClick={onClose}
                            />

                        </div>
                    }
                    {
                        showCapture &&
                        props.invoice.type === 'spu' &&
                        <div className='fdc hfill mh0 mw0'>
                            <SPUCapturingTable
                                invoices={[selectedInvoice] as Array<ITransformerInvoice<ISPULine>>}
                                disabledTransformers={[]}
                                multi={props.invoice.lines.length > 1}
                                onSubmit={onCaptureSubmit}
                                onCancelClick={onClose}
                            />

                        </div>
                    }
                    {
                        !showCapture &&
                        <div className='fdc flx1 mh0 mw0'>
                            <TariffConfirmTable
                                invoices={[selectedInvoice] as Array<ITransformerInvoice<ISPULine>>}
                                group={props.invoice.group}
                                disabledTransformers={[]}
                                onCancelClick={onCancelClick}
                                onConfirmClick={onConfirmClick}
                            />
                        </div>
                    }
                </DialogContent>
            </ProjectDialog>
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
    };
};

const TransformerEditDialog = connect(
    mapStateToProps,
)(TransformerEditDialogComponent);

export default TransformerEditDialog;
